import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { toast } from 'sonner';
import { artifactClient } from '../lib/connect/clients';
import { createPendingRow, isZipFile, replaceRow, retryRow, submitBatch } from '../lib/upload';
import { expandArchiveArtifactViaTesoro, uploadAttachmentViaTesoro } from '../lib/uploadAttachment';
import { fetchJobStatus, jobStatusQueryKey } from './useJobStatus';
/**
 * Not inlined as a bare `expandArchiveOption ?? expandArchiveArtifactViaTesoro` at each call
 * site — that would dereference the import the moment the `??` runs. Every test that mocks
 * '../lib/uploadAttachment' without that export (every batch-upload test that predates
 * archives) would then throw on a plain-file submit that never touches the archive path at
 * all. Wrapping it in a function defers the dereference to the one call site that's a ZIP.
 */ const defaultExpandArchive = (artifactId, options)=>expandArchiveArtifactViaTesoro(artifactId, options);
/** Groups skipped archive members by archive so a batch spanning more than one ZIP stays legible. */ function describeSkippedMembers(skipped) {
    const byArchive = new Map();
    for (const member of skipped){
        const members = byArchive.get(member.archiveName) ?? [];
        members.push(member);
        byArchive.set(member.archiveName, members);
    }
    return Array.from(byArchive.entries()).map(([archiveName, members])=>{
        const details = members.map((member)=>`${member.path}: ${member.reason}`).join('; ');
        return `${members.length} file${members.length === 1 ? '' : 's'} skipped in ${archiveName}: ${details}`;
    }).join(' ');
}
/**
 * Owns the batch-upload rows array and binds the framework-free orchestration
 * in `lib/upload` to React: localStorage persist/restore, the React Query
 * job-status cache, and the toasts the composer's failure gates already show.
 */ export function useAttachmentUploads(options = {}) {
    const queryClient = useQueryClient();
    const [rows, setRows] = useState([]);
    // Lets retry/submit always read the latest rows without depending on
    // `rows` in their own useCallback deps — that would mint a fresh function
    // identity on every row change, which would make a `clear`/`submit`
    // reference unstable in any consumer's own useEffect deps.
    const rowsRef = useRef(rows);
    const operationsRef = useRef(new Map());
    const writeRows = useCallback((next)=>{
        rowsRef.current = next;
        setRows(next);
    }, []);
    // Surface-scoped so /assistant and /proposal/<id>/v2/home don't bleed into
    // each other. Captured once at mount via useRef, same as the prior
    // ComposerInput-local implementation.
    const pendingAttachmentsKeyRef = useRef(options.persistKey ?? (typeof window !== 'undefined' ? `pendingAttachments::${window.location.pathname}` : 'pendingAttachments::ssr'));
    const pendingAttachmentsKey = options.persistKey ?? pendingAttachmentsKeyRef.current;
    const restoredScopeRef = useRef(null);
    useEffect(()=>{
        if (restoredScopeRef.current === pendingAttachmentsKey) return;
        const changingScope = restoredScopeRef.current !== null;
        restoredScopeRef.current = pendingAttachmentsKey;
        if (changingScope) {
            operationsRef.current.clear();
            writeRows([]);
        }
        if (typeof window === 'undefined') return;
        try {
            const raw = window.localStorage.getItem(pendingAttachmentsKey);
            if (!raw) return;
            const parsed = JSON.parse(raw);
            if (!Array.isArray(parsed) || parsed.length === 0) return;
            const restored = parsed.filter((r)=>typeof r.filename === 'string' && (r.jobId || r.attachmentId)).map((r)=>{
                // Synthetic File for display — its bytes are never read after
                // restore. We override .size with the persisted byte count so the
                // row renders "430 KB" instead of "0 B".
                const file = new File([], r.filename, {
                    type: r.contentType ?? 'application/octet-stream'
                });
                if (typeof r.size === 'number' && r.size > 0) {
                    Object.defineProperty(file, 'size', {
                        value: r.size
                    });
                }
                return createPendingRow(file, {
                    progress: 100,
                    status: r.isArchive ? 'error' : 'uploaded',
                    contentType: r.contentType,
                    jobId: r.jobId ?? null,
                    attachmentId: r.attachmentId,
                    processingStatus: r.processingStatus,
                    useDeepProcessing: r.useDeepProcessing,
                    outputFormat: r.outputFormat,
                    sourceArchiveName: r.sourceArchiveName,
                    isArchive: r.isArchive,
                    statusLabel: r.isArchive ? 'Unpacking was interrupted. Retry or remove this archive.' : undefined
                });
            });
            if (restored.length > 0) {
                writeRows([
                    ...rowsRef.current,
                    ...restored
                ]);
            }
        } catch  {
            // Corrupted localStorage payload — drop it rather than fail the mount.
            try {
                window.localStorage.removeItem(pendingAttachmentsKey);
            } catch  {
            // ignore
            }
        }
    }, [
        pendingAttachmentsKey,
        writeRows
    ]);
    useEffect(()=>{
        if (typeof window === 'undefined') return;
        if (restoredScopeRef.current !== pendingAttachmentsKey || rowsRef.current !== rows) return;
        const persistable = rows.filter((r)=>r.jobId || r.attachmentId).map((r)=>({
                filename: r.file.name,
                size: r.file.size,
                contentType: r.contentType,
                jobId: r.jobId,
                attachmentId: r.attachmentId,
                processingStatus: r.processingStatus,
                useDeepProcessing: r.useDeepProcessing,
                outputFormat: r.outputFormat,
                sourceArchiveName: r.sourceArchiveName,
                isArchive: r.isArchive
            }));
        try {
            if (persistable.length === 0) {
                window.localStorage.removeItem(pendingAttachmentsKey);
            } else {
                window.localStorage.setItem(pendingAttachmentsKey, JSON.stringify(persistable));
            }
        } catch  {
        // Quota exceeded / private mode — non-fatal.
        }
    }, [
        rows,
        pendingAttachmentsKey
    ]);
    const addRow = useCallback((row)=>{
        writeRows([
            ...rowsRef.current,
            row
        ]);
    }, [
        writeRows
    ]);
    const updateRow = useCallback((id, updater)=>{
        writeRows(replaceRow(rowsRef.current, id, updater));
    }, [
        writeRows
    ]);
    const removeRow = useCallback((id)=>{
        writeRows(rowsRef.current.filter((r)=>r.id !== id));
    }, [
        writeRows
    ]);
    // Forces every intermediate state to commit synchronously, so a terminal
    // "ready" render is never silently batched away with whatever state
    // update runs right after it settles (SAD-741's vanishing-success defect).
    const createPublisher = useCallback((initial)=>{
        let previous = initial;
        const scope = restoredScopeRef.current;
        return (next)=>{
            if (restoredScopeRef.current !== scope) return;
            const before = new Map(previous.map((row)=>[
                    row.id,
                    row
                ]));
            const after = new Map(next.map((row)=>[
                    row.id,
                    row
                ]));
            const additions = next.filter((row)=>!before.has(row.id));
            let inserted = false;
            const current = rowsRef.current.flatMap((row)=>{
                // Only apply changes this operation made. Concurrent additions,
                // retries and removals belong to their own operation.
                if (!before.has(row.id)) return [
                    row
                ];
                const replacement = after.get(row.id);
                if (!replacement) {
                    if (!inserted) {
                        inserted = true;
                        return additions;
                    }
                    return [];
                }
                return [
                    replacement === before.get(row.id) ? row : replacement
                ];
            });
            previous = next;
            // If an archive was removed while the RPC was pending, its children
            // have no surviving parent position and must not reappear.
            flushSync(()=>writeRows(current));
        };
    }, [
        writeRows
    ]);
    const { transport: transportOption, retryProcessing: retryProcessingOption, startProcessing: startProcessingOption, fetchJobStatus: fetchJobStatusOption, expandArchive: expandArchiveOption } = options;
    const retry = useCallback(async (id, context)=>{
        const existing = operationsRef.current.get(id);
        if (existing) return existing;
        const initial = rowsRef.current;
        const transport = transportOption ?? uploadAttachmentViaTesoro;
        const expandArchiveRpc = expandArchiveOption ?? defaultExpandArchive;
        const retryProcessingRpc = retryProcessingOption ?? (async (jobId)=>{
            const resp = await artifactClient.retryProcessing({
                jobId
            });
            return {
                jobId: resp.job?.jobId ?? null
            };
        });
        const operation = (async ()=>{
            const result = await retryRow(initial, id, context, {
                transport,
                retryProcessing: retryProcessingRpc,
                expandArchive: expandArchiveRpc,
                startProcessing: startProcessingOption ?? (async (artifactId)=>{
                    const response = await artifactClient.startArtifactProcessing({
                        artifactId
                    });
                    return {
                        jobId: response.job?.jobId ?? null
                    };
                })
            }, createPublisher(initial));
            // Only the processing-retry and archive-expand branches toast on
            // failure — matches the prior behaviour, where an upload-retry failure
            // just left the row in 'error' with no extra toast.
            if (result.skipped?.length) toast.warning(describeSkippedMembers(result.skipped));
            if (!result.ok && (result.kind === 'processing' || result.kind === 'expand')) {
                toast.error(result.error);
            }
        })();
        operationsRef.current.set(id, operation);
        try {
            await operation;
        } finally{
            operationsRef.current.delete(id);
        }
    }, [
        transportOption,
        retryProcessingOption,
        startProcessingOption,
        expandArchiveOption,
        createPublisher
    ]);
    const addFiles = useCallback((files, context)=>{
        const added = files.map((file)=>createPendingRow(file, {
                isArchive: isZipFile(file)
            }));
        writeRows([
            ...rowsRef.current,
            ...added
        ]);
        if (context) {
            for (const row of added){
                if (row.isArchive) void retry(row.id, context);
            }
        }
    }, [
        retry,
        writeRows
    ]);
    const submit = useCallback(async (context, opts)=>{
        if (operationsRef.current.size > 0) await Promise.all(operationsRef.current.values());
        if (rowsRef.current.length === 0) return {
            ok: true,
            attachments: []
        };
        const transport = transportOption ?? uploadAttachmentViaTesoro;
        const expandArchiveRpc = expandArchiveOption ?? defaultExpandArchive;
        const fetchJobStatusRpc = fetchJobStatusOption ?? ((jobId)=>queryClient.fetchQuery({
                queryKey: jobStatusQueryKey(jobId),
                queryFn: ()=>fetchJobStatus(jobId),
                // Fresh for 1s: matches the legacy 2s poll cadence closely enough
                // that the cache absorbs concurrent hits from the per-row
                // useJobStatus poll without ever going stale mid-iteration.
                staleTime: 1000
            }));
        const initial = rowsRef.current;
        const result = await submitBatch(initial, context, {
            transport,
            expandArchive: expandArchiveRpc,
            fetchJobStatus: fetchJobStatusRpc,
            onPollStart: opts?.onPollStart
        }, createPublisher(initial));
        if (result.skipped.length > 0) {
            toast.warning(describeSkippedMembers(result.skipped));
        }
        if (!result.ok) {
            const copy = result.reason === 'upload-failed' ? 'One or more attachments failed to upload. Retry or remove them, then send.' : result.reason === 'processing-failed' ? 'One or more attachments failed to process. Retry or remove them, then send.' : 'Processing is still running. Try again in a moment, or remove the attachment.';
            toast.error(copy);
            return {
                ok: false
            };
        }
        const retained = new Set(rowsRef.current.map((row)=>row.attachmentId));
        return {
            ok: true,
            attachments: result.attachments.filter((attachment)=>retained.has(attachment.id))
        };
    }, [
        transportOption,
        fetchJobStatusOption,
        expandArchiveOption,
        queryClient,
        createPublisher
    ]);
    const clear = useCallback(()=>writeRows([]), [
        writeRows
    ]);
    const failedFileCount = useMemo(()=>rows.filter((r)=>r.status === 'error' || r.processingStatus === 'error').length, [
        rows
    ]);
    const pendingFileCount = useMemo(()=>rows.filter((r)=>r.status === 'uploading' || r.status === 'expanding' || r.status === 'pending' || r.processingStatus === 'processing' || r.processingStatus === 'queued').length, [
        rows
    ]);
    return {
        rows,
        addFiles,
        addRow,
        updateRow,
        removeRow,
        retry,
        submit,
        clear,
        failedFileCount,
        pendingFileCount,
        hasFailedAttachments: failedFileCount > 0,
        hasPendingAttachments: pendingFileCount > 0
    };
}
